import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Input, InputNumber, Modal, Space, Popconfirm, message, Switch, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { makePostCall, getUserDetails } from '../../utils/helper';
import ShortcutBuilder from '../../components/ShortcutBuilder';
import { summarizeShortcut } from '../../utils/shortcuts';

const emptyForm = { qf_id: null, qf_name: '', qf_filter_json: {}, qf_order: 0, qf_active: true };

/**
 * Admin master-shortcut management. Shortcuts created here become available for
 * every user to enable in their "Shortcut Settings" dialog.
 */
const ShortcutsAdmin = () => {
  const [list, setList] = useState({ data: [], loading: false });
  const [modal, setModal] = useState({ visible: false, form: emptyForm });
  const [saving, setSaving] = useState(false);
  const userDetails = getUserDetails();

  const fetchList = useCallback(async () => {
    setList((prev) => ({ ...prev, loading: true }));
    try {
      const res = await makePostCall('/admin/list-quick-filters', {});
      setList({ data: res.data?.data || [], loading: false });
    } catch (e) {
      setList({ data: [], loading: false });
      message.error('Failed to load shortcuts');
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openCreate = () => setModal({ visible: true, form: { ...emptyForm } });

  const openEdit = (record) => {
    let parsed = {};
    try {
      parsed = record.qf_filter_json ? JSON.parse(record.qf_filter_json) : {};
    } catch (e) {
      parsed = {};
    }
    setModal({
      visible: true,
      form: {
        qf_id: record.qf_id,
        qf_name: record.qf_name,
        qf_filter_json: parsed,
        qf_order: record.qf_order || 0,
        qf_active: record.qf_active !== false,
      },
    });
  };

  const closeModal = () => setModal({ visible: false, form: emptyForm });

  const handleSave = async () => {
    const { form } = modal;
    if (!form.qf_name?.trim()) {
      message.warning('Please enter a shortcut name');
      return;
    }
    if (!form.qf_filter_json || Object.keys(form.qf_filter_json).length === 0) {
      message.warning('Please add at least one filter');
      return;
    }
    setSaving(true);
    try {
      await makePostCall('/admin/save-quick-filter', {
        qf_id: form.qf_id || undefined,
        qf_name: form.qf_name.trim(),
        qf_filter_json: JSON.stringify(form.qf_filter_json),
        qf_order: form.qf_order || 0,
        qf_active: form.qf_active,
        created_by: userDetails?.username || userDetails?.user_id,
      });
      message.success(form.qf_id ? 'Shortcut updated' : 'Shortcut created');
      closeModal();
      fetchList();
    } catch (e) {
      message.error('Failed to save shortcut');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      await makePostCall('/admin/delete-quick-filter', { qf_id: record.qf_id });
      message.success('Shortcut deleted');
      fetchList();
    } catch (e) {
      message.error('Failed to delete shortcut');
    }
  };

  const handleToggleActive = async (record, checked) => {
    try {
      await makePostCall('/admin/save-quick-filter', {
        qf_id: record.qf_id,
        qf_name: record.qf_name,
        qf_filter_json: record.qf_filter_json || JSON.stringify({}),
        qf_order: record.qf_order || 0,
        qf_active: checked,
      });
      fetchList();
    } catch (e) {
      message.error('Failed to update shortcut');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'qf_name',
      key: 'qf_name',
      sorter: (a, b) => (a.qf_name || '').localeCompare(b.qf_name || ''),
    },
    {
      title: 'Filters',
      key: 'summary',
      render: (_, record) => (
        <span style={{ color: '#475569' }}>
          {summarizeShortcut(record.qf_filter_json)}
        </span>
      ),
    },
    {
      title: 'Order',
      dataIndex: 'qf_order',
      key: 'qf_order',
      width: 90,
      sorter: (a, b) => (a.qf_order || 0) - (b.qf_order || 0),
    },
    {
      title: 'Active',
      dataIndex: 'qf_active',
      key: 'qf_active',
      width: 100,
      render: (active, record) => (
        <Switch
          checked={active !== false}
          onChange={(checked) => handleToggleActive(record, checked)}
        />
      ),
    },
    {
      title: 'Type',
      key: 'type',
      width: 110,
      render: (_, record) =>
        record.qf_filter_json ? (
          <Tag color="blue">Compound</Tag>
        ) : (
          <Tag>Legacy</Tag>
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this shortcut?"
            description="It will also be removed from all users who enabled it."
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="admin-container" style={{ padding: 16 }}>
      <div
        className="admin-header"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}
      >
        <h2 style={{ margin: 0 }}>Shortcuts</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Add Shortcut
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={list.data}
        loading={list.loading}
        rowKey="qf_id"
        pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `Total ${t} shortcuts` }}
        scroll={{ x: 'max-content' }}
      />

      <Modal
        title={modal.form.qf_id ? 'Edit Shortcut' : 'Add Shortcut'}
        open={modal.visible}
        onCancel={closeModal}
        onOk={handleSave}
        confirmLoading={saving}
        okText="Save"
        width={520}
        destroyOnClose
      >
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
            Shortcut Name
          </label>
          <Input
            placeholder="e.g. CT/MR unreported today"
            value={modal.form.qf_name}
            onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, qf_name: e.target.value } }))}
          />
        </div>

        <ShortcutBuilder
          value={modal.form.qf_filter_json}
          onChange={(json) => setModal((m) => ({ ...m, form: { ...m.form, qf_filter_json: json } }))}
        />

        <div style={{ display: 'flex', gap: 24, alignItems: 'center', marginTop: 8 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
              Display Order
            </label>
            <InputNumber
              min={0}
              value={modal.form.qf_order}
              onChange={(val) => setModal((m) => ({ ...m, form: { ...m.form, qf_order: val ?? 0 } }))}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
              Active
            </label>
            <Switch
              checked={modal.form.qf_active}
              onChange={(checked) => setModal((m) => ({ ...m, form: { ...m.form, qf_active: checked } }))}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ShortcutsAdmin;
